/**
 * Haversine distance between two GPS coordinates (returns kilometers)
 */
export const haversineKm = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Extract lat/lon from a location history entry that may have different formats:
 *  - { location: [longitude, latitude] }
 *  - { vehicleInfo: { location: [longitude, latitude] } }
 *  - { latitude, longitude }
 *  - { lat, lng }
 */
export const extractCoords = (
  entry: any
): { lat: number; lon: number } | null => {
  if (!entry) return null;
  if (entry.location && Array.isArray(entry.location) && entry.location.length >= 2) {
    return { lon: entry.location[0], lat: entry.location[1] };
  }
  const loc = entry.vehicleInfo?.location;
  if (loc && Array.isArray(loc) && loc.length >= 2) {
    return { lon: loc[0], lat: loc[1] };
  }
  if (entry.latitude != null && entry.longitude != null) {
    return { lat: entry.latitude, lon: entry.longitude };
  }
  if (entry.lat != null && entry.lng != null) {
    return { lat: entry.lat, lon: entry.lng };
  }
  return null;
};

/**
 * Build cumulative distance array (km) from a location history / route array.
 * Returns an array of the same length, where index i = total km from start to point i.
 */
export const cumulativeDistancesKm = (history: any[]): number[] => {
  const distances: number[] = [];
  let cumulative = 0;
  for (let i = 0; i < history.length; i++) {
    if (i > 0) {
      const prev = extractCoords(history[i - 1]);
      const curr = extractCoords(history[i]);
      if (prev && curr) {
        const segment = haversineKm(prev.lat, prev.lon, curr.lat, curr.lon);
        cumulative += segment;
      }
    }
    distances.push(cumulative);
  }
  return distances;
};

/**
 * Total distance (km) from a location history / route array.
 */
export const totalDistanceKm = (history: any[]): number => {
  if (!history || history.length < 2) return 0;
  const cum = cumulativeDistancesKm(history);
  return cum[cum.length - 1];
};
